import { get } from '@/utils/request'
import { useMemo, useRef } from 'react'
import useSWR from 'swr'
import { forEach } from 'lodash-es'
import { debouncedMessage } from '@/utils/debouncedFunction'
import { matchType } from '@/utils/types'
import { commonSWROptions, focusedSWROptions } from './useAdmin'

const getMatchesCount = (mode?: string) =>
  get('matches/matches_count', { mode }).then((res) => {
    if (res.ok) {
      return res.data
    }
    return { match_count_group: [], player_count_group: [] }
  })

export const useMatches = (page: number, pageSize: number) => {
  const getAllMatches = () =>
    get('matches', { page, pageSize }).then((res) => {
      if (res.ok) {
        return { matches: res.matches as matchType[], sum: res.total }
      }
      return { matches: [], sum: 0 }
    })

  return useSWR(`matches-${page}-${pageSize}`, getAllMatches, { fallbackData: { matches: [], sum: 0 }, ...commonSWROptions })
}

const getAllMatchesWithDeleted = () =>
  get('matches/all_matches_with_deleted').then((res) => {
    if (res.ok) {
      return res.matches as matchType[]
    }
    return []
  })

export const useMatchesWithDeleted = () => {
  return useSWR('matches_with_deleted', getAllMatchesWithDeleted, { fallbackData: [], ...commonSWROptions })
}

export const useMatchedCount = (mode?: string) => {
  return useSWR(mode ? `matches_count/${mode}` : null, () => getMatchesCount(mode), { fallbackData: { match_count_group: [], player_count_group: [] }, ...commonSWROptions })
}

const getMatchesList = (page, pageSize, matchesType, filterKeywords, sort?) =>
  get('matches/matches_list', { page, pageSize, matchesType, filterKeywords, sort }).then((res) => {
    if (res.ok) {
      return { matches: res.matches as matchType[], sum: res.sum }
    }
    return { matches: [], sum: 0 }
  })

const getOnlineMatchesList = () =>
  get('matches/entryable_online_game').then((res) => {
    if (res.ok) {
      return { matches: res.matches as matchType[] }
    }
    return { matches: [] }
  })

export const useMatcheslist = (page, pageSize, matchesType, filterKeywords, sort?) => {
  const swr = useSWR(`matches-list-${page}-${pageSize}-${matchesType}-${filterKeywords}-${sort || ''}`, () => getMatchesList(page, pageSize, matchesType, filterKeywords, sort), {
    fallbackData: { matches: [], sum: 0 },
    ...commonSWROptions,
  })
  // keepPreviousData(SWR 1.x 无此选项,手动实现):翻页时 key 变为新 key、命中空 fallback,
  // 若沿用会整屏闪成空白(红色背景透出)。用上一页已有数据占位,新数据到达再替换,滚动/翻页更顺滑。
  const lastNonEmpty = useRef(swr.data)
  if (swr.data && swr.data.matches && swr.data.matches.length > 0) {
    lastNonEmpty.current = swr.data
  }
  const isEmptyWhileLoading = swr.isValidating && (!swr.data || !swr.data.matches || swr.data.matches.length === 0)
  const data = isEmptyWhileLoading ? lastNonEmpty.current : swr.data
  return { ...swr, data }
}

export const useOnlineMatcheslist = () => {
  return useSWR(`online-matches-list`, () => getOnlineMatchesList(), {
    fallbackData: { matches: [] },
    ...commonSWROptions,
  })
}

const getMatchWithSubMatches = (id: string) =>
  get('matches/multi', { id }).then((res) => {
    if (res.ok) {
      return res.matches
    }
    return []
  })

export const useMultiMatches = (id: string) => {
  return useSWR(id ? `multi-match-${id}` : undefined, () => getMatchWithSubMatches(id), { fallbackData: [], ...commonSWROptions })
}

const getWechatEntryableMatches = () =>
  get('matches/entryable').then((res) => {
    if (res.ok) {
      return res.matches
    }
    return []
  })

export const useWechatEntryableMatches = () => {
  return useSWR(`entryable-match`, () => getWechatEntryableMatches(), { fallbackData: [], ...commonSWROptions })
}

const getTodayMatch = () =>
  get('matches/today').then((res) => {
    if (res.ok) {
      return res.matches
    }
    return []
  })

export const useTodayMatches = () => {
  return useSWR(`today-match`, () => getTodayMatch(), { fallbackData: [], ...commonSWROptions })
}

const fetchMatch = (match_id: string) =>
  get(`matches/${match_id}`).then((res) => {
    if (res.ok) {
      return res.match
    }
    return { ...res, ok: false }
  })

const fetchMatchByShortId = (short_id: string) =>
  get(`matches/match_by_short_id`, { short_id }).then((res) => {
    if (res.ok) {
      return res.match
    }
    return { ...res, ok: false }
  })

export const useMatch = (match_id, needReloadWhenRefocus = false, is_online_game = false) => {
  const options = needReloadWhenRefocus ? focusedSWROptions : commonSWROptions
  const matchSWR = useSWR(match_id ? `match-${match_id}` : undefined, () => fetchMatch(match_id), {
    fallbackData: {},
    ...options,
    refreshInterval: is_online_game ? 60000 : 900000,
  }) as { data: matchType; mutate: any; isValidating: boolean }

  if (matchSWR.data.ok === false && matchSWR.data.message) {
    debouncedMessage(matchSWR.data.message, 'warning')
  }

  // 起始值为0, +1后与比赛轮次一致
  const timer_turn = useMemo(() => {
    let this_turn = 0
    forEach(matchSWR?.data?.turn_data ?? [], (t) => {
      if (t.start_time) this_turn += 0.5
      if (t.end_time) this_turn += 0.5
    })
    return this_turn
  }, [matchSWR?.data?.turn_data])

  if (matchSWR?.data && Object.keys(matchSWR.data).length > 0) {
    return { ...matchSWR, data: { ...matchSWR.data, timer_turn } }
  }
  return matchSWR
}

export const useMatchByShortId = (short_id: string) => {
  const options = commonSWROptions
  const { data, mutate, isValidating } = useSWR(short_id ? `match-${short_id}` : undefined, () => fetchMatchByShortId(short_id), {
    fallbackData: {},
    ...options,
  }) as { data: matchType; mutate: any; isValidating: boolean }

  return { data, mutate, isValidating }
}

const getClubMatches = (club) =>
  get(`tianhehui/matches`, { club }).then((res) => {
    if (res.ok) {
      return res.matches as matchType[]
    }
    return []
  })

export const useClubMatches = (club) => {
  return useSWR(club ? `${club}-matches` : null, () => getClubMatches(club), { fallbackData: [], ...commonSWROptions })
}
